import {
  getClientLocaleLoadPath,
  I18nHydrationScript,
  useI18n,
} from "@brightdotai/digital-water-i18n";
import {
  LinksProvider,
  RootLayout,
  TimeZoneProvider,
  ToastProvider,
} from "@brightdotai/digital-water-ui";
import { useContext, useEffect } from "react";
import {
  href,
  Link,
  Links,
  Meta,
  NavLink,
  Outlet,
  Scripts,
  ScrollRestoration,
  useSubmit,
  type HeadersArgs,
  type MetaFunction,
  type ShouldRevalidateFunction,
} from "react-router";
import type { Route } from "./+types/root";
import { DEFAULT_LOCALE, getPreloadedNamespaces } from "./i18n";
import { inferClientHints } from "./modules/client-hints/client-hints.client";
import { getClientHints } from "./modules/client-hints/client-hints.server";
import {
  SentryConfigurationContext,
  SentryConfigurationScript,
} from "./modules/observability/sentry";
import "./tailwind.css";
import { useNonce } from "./utils/use-nonce";

export const meta: MetaFunction = () => {
  return [{ title: "Azuria Portal" }];
};
export function headers({ parentHeaders }: HeadersArgs) {
  parentHeaders.append("X-Content-Type-Options", "nosniff");
  parentHeaders.append("X-Frame-Options", "DENY");

  // set as default in the root component, these will apply by default
  // these can be overwritten in child components eg.
  // Cache-Control: private, max-age=300
  parentHeaders.set("Cache-Control", "no-store, no-cache");
  parentHeaders.set("Expires", "0");
  parentHeaders.set("Pragma", "no-cache");

  return parentHeaders;
}

export const shouldRevalidate: ShouldRevalidateFunction = () => false;

export const loader = async ({ request }: Route.LoaderArgs) => {
  let region = "uw2";
  let clientHints = await getClientHints(request);

  return {
    clientHints,
    region,
  };
};

export default function Component(props: Route.ComponentProps) {
  let { locale } = useI18n();
  const sentryConfiguration = useContext(SentryConfigurationContext);
  const nonce = useNonce();
  let submit = useSubmit();

  useEffect(
    function initializeClientHints() {
      if (props.loaderData.clientHints) {
        return;
      }

      void submit(
        {
          clientHints: inferClientHints(),
          redirectTo: `${window.location.pathname}${window.location.search}`,
        },
        {
          action: href("/actions/set-client-hints"),
          method: "POST",
          encType: "application/json",
        },
      );
    },
    [props.loaderData.clientHints, submit],
  );

  const localeFiles = getPreloadedNamespaces().map((namespace) => (
    <link
      key={namespace}
      nonce={nonce}
      rel={"preload"}
      href={getClientLocaleLoadPath("/locales", DEFAULT_LOCALE, namespace, __VERSION__)}
      as={"fetch"}
      crossOrigin={"anonymous"}
    />
  ));

  return (
    <RootLayout.Html
      lang={locale}
      nonce={nonce}
      theme={"dark"} // TODO: default to dark mode, need to build dark component switch
    >
      <TimeZoneProvider timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}>
        <LinksProvider Link={Link} NavLink={NavLink}>
          <head>
            <meta charSet={"utf-8"} />
            <meta name={"viewport"} content={"width=device-width, initial-scale=1"} />
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{ __html: `window.__nonce = '${nonce}'` }}
            />

            <Meta />
            <link
              nonce={nonce}
              href={"https://fonts.googleapis.com/css?family=Public+Sans:700,300"}
              rel={"stylesheet"}
            />
            {localeFiles}
            <Links />
          </head>
          <RootLayout.Body
            className={"dark:text-gray-dark-on-surface text-gray-light-on-surface"}
          >
            <ToastProvider>
              <Outlet />
            </ToastProvider>
            <ScrollRestoration nonce={nonce} />
            <Scripts nonce={nonce} />
            <I18nHydrationScript
              nonce={nonce}
              locale={locale}
              region={props.loaderData.region}
            />

            {sentryConfiguration && (
              <SentryConfigurationScript
                nonce={nonce}
                sentryConfiguration={sentryConfiguration}
              />
            )}
          </RootLayout.Body>
        </LinksProvider>
      </TimeZoneProvider>
    </RootLayout.Html>
  );
}
